import { useState } from 'react';
import SEO from '@/components/base/SEO';
import Breadcrumb from '@/components/base/Breadcrumb';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [formMessage, setFormMessage] = useState('');

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '韩漫网站',
    url: 'https://hanman.example.com',
    description: '韩漫网站——最全的韩国漫画免费在线阅读平台，提供海量韩国漫画资源。',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'contact@hanman.example.com',
      availableLanguage: ['Chinese'],
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '韩漫网站是做什么的？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '韩漫网站是一个专业的韩国漫画免费在线阅读平台，致力于为广大漫画爱好者提供最新、最全的韩国漫画资源。平台涵盖恋爱、奇幻、动作、悬疑、都市、历史、搞笑、恐怖等八大分类，每日更新海量内容。',
        },
      },
      {
        '@type': 'Question',
        name: '韩漫网站有什么优势？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '韩漫网站具备以下核心优势：1）韩漫大全资源丰富，收录上万部韩国漫画；2）完全免费，无需付费即可在线阅读所有漫画；3）界面简洁，阅读体验流畅；4）每日更新，确保读者第一时间看到最新内容；5）分类清晰，通过韩漫网址快速导航到任意分类。',
        },
      },
      {
        '@type': 'Question',
        name: '如何联系韩漫网站？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '您可以通过本页面的联系我们表单与韩漫网站团队取得联系。我们将尽快回复您的咨询、建议或合作意向。',
        },
      },
    ],
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget as HTMLFormElement;
    const honeypot = (form.elements.namedItem('phone_alt') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus('success');
      setFormMessage('感谢您的留言，我们会尽快回复！');
      form.reset();
      return;
    }

    setFormStatus('loading');
    setFormMessage('');

    try {
      const formData = new FormData(form);
      const urlEncoded = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'phone_alt') {
          urlEncoded.append(key, value as string);
        }
      });

      const response = await fetch('https://readdy.ai/api/form/d9oscrqftmtnfcfh0d60', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: urlEncoded.toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg = parsed?.meta?.message || parsed?.message || responseText;

      if (!response.ok || (parsed && parsed.code !== 'OK')) {
        setFormStatus('error');
        setFormMessage(typeof serverMsg === 'string' ? serverMsg : '提交失败，请稍后再试');
        return;
      }

      setFormStatus('success');
      setFormMessage('感谢您的留言，我们会尽快回复！');
      form.reset();
    } catch {
      setFormStatus('error');
      setFormMessage('网络错误，请稍后重试');
    }
  };

  const stats = [
    { value: '10,000+', label: '漫画资源' },
    { value: '500万+', label: '月活用户' },
    { value: '8', label: '内容分类' },
    { value: '99.7%', label: '用户好评率' },
  ];

  const testimonials = [
    {
      name: '漫画迷小李',
      role: '忠实用户 · 3年',
      content: '韩漫网站是我每天必刷的平台，资源真的很全！之前找了好久的一部小众韩漫，居然在这里找到了，太惊喜了。而且完全免费，良心网站！',
      rating: 5,
    },
    {
      name: '追漫达人阿华',
      role: 'VIP用户 · 2年',
      content: '更新速度是真的快，基本上韩漫出了新的这边同步就更了。界面也很清爽，没有乱七八糟的广告，阅读体验特别好。强烈推荐韩漫网址！',
      rating: 5,
    },
    {
      name: '二次元少女',
      role: '活跃用户 · 1年',
      content: '分类做得特别细致，恋爱、奇幻、悬疑什么类型都有。我最喜欢悬疑分类，完美不在场证明追了好久了。韩漫大全，值得拥有！',
      rating: 5,
    },
  ];

  return (
    <>
      <SEO
        title="关于我们 - 韩漫网站"
        description="了解韩漫网站的品牌故事、核心优势与用户口碑。韩漫网站——最全的韩国漫画免费在线阅读平台，海量韩漫资源一网打尽。联系我们获取更多信息。"
        keywords="韩漫网站,关于我们,韩国漫画免费,韩漫网址,韩漫大全,联系我们"
        canonicalUrl="https://hanman.example.com/about"
      />
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />

        <section id="brand" className="py-8 md:py-12">
          <div className="text-center max-w-3xl mx-auto mb-10">
            <h1 className="font-heading text-3xl md:text-4xl font-black text-foreground-950 mb-4">
              关于韩漫网站
            </h1>
            <p className="text-foreground-600 leading-relaxed">
              韩漫网站成立于2024年，是中国领先的韩国漫画免费在线阅读平台。我们致力于为广大漫画爱好者
              提供最新、最全的韩国漫画资源，通过韩漫网址实现便捷导航，让每一位用户都能在韩漫大全中找到
              自己喜爱的漫画作品。
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-12">
            {stats.map((stat) => (
              <div key={stat.label} className="bg-background-100 rounded-xl p-5 text-center">
                <div className="font-heading text-2xl md:text-3xl font-black text-primary-500 mb-1">
                  {stat.value}
                </div>
                <div className="text-sm text-foreground-500">{stat.label}</div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-background-100 rounded-2xl p-6 md:p-8">
              <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center mb-4">
                <i className="ri-eye-line text-xl text-primary-500"></i>
              </div>
              <h3 className="font-heading text-lg font-bold text-foreground-950 mb-2">我们的愿景</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">
                成为全球最大的韩漫阅读社区，让每一位漫画爱好者都能轻松找到并阅读自己喜欢的韩国漫画。
                我们希望通过韩漫网址的便捷导航和韩漫大全的丰富内容，打造最优质的漫画阅读体验。
              </p>
            </div>
            <div className="bg-background-100 rounded-2xl p-6 md:p-8">
              <div className="w-12 h-12 rounded-xl bg-accent-100 flex items-center justify-center mb-4">
                <i className="ri-star-line text-xl text-accent-500"></i>
              </div>
              <h3 className="font-heading text-lg font-bold text-foreground-950 mb-2">我们的使命</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">
                让优质漫画触手可及。我们坚持免费模式，打破付费壁垒，让更多人能够享受韩国漫画的精彩世界。
                不断丰富韩漫大全内容库，持续优化韩漫网址导航体验。
              </p>
            </div>
          </div>
        </section>

        <section id="reputation" className="py-8 md:py-12 border-t border-background-200">
          <div className="text-center max-w-3xl mx-auto mb-10">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
              用户口碑
            </h2>
            <p className="text-sm text-foreground-500">
              听听我们的用户怎么说
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {testimonials.map((t) => (
              <div key={t.name} className="bg-background-100 rounded-2xl p-6">
                <div className="flex text-accent-500 text-sm mb-4">
                  {Array.from({ length: t.rating }).map((_, i) => (
                    <i key={i} className="ri-star-fill"></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  &ldquo;{t.content}&rdquo;
                </p>
                <div>
                  <p className="text-sm font-medium text-foreground-900">{t.name}</p>
                  <p className="text-xs text-foreground-500">{t.role}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section id="contact" className="py-8 md:py-12 border-t border-background-200">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-3">
                联系我们
              </h2>
              <p className="text-sm text-foreground-500">
                有任何问题、建议或合作意向，欢迎通过以下表单与我们联系
              </p>
            </div>

            <form
              onSubmit={handleSubmit}
              data-readdy-form
              className="bg-background-100 rounded-2xl p-6 md:p-8 space-y-5"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    姓名 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-all"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    邮箱 <span className="text-primary-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-all"
                    placeholder="请输入您的邮箱"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  主题 <span className="text-primary-500">*</span>
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  required
                  className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-all"
                  placeholder="请输入留言主题"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  留言内容 <span className="text-primary-500">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={5}
                  maxLength={500}
                  className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 transition-all resize-none"
                  placeholder="请输入您的留言（最多500字）"
                ></textarea>
                <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
              </div>

              <div className="anti-spam-field">
                <input
                  type="text"
                  name="phone_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />
              </div>

              {formStatus === 'success' && (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-green-50 text-green-700 text-sm">
                  <i className="ri-checkbox-circle-line"></i>
                  {formMessage}
                </div>
              )}
              {formStatus === 'error' && (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 text-red-700 text-sm">
                  <i className="ri-error-warning-line"></i>
                  {formMessage}
                </div>
              )}

              <button
                type="submit"
                disabled={formStatus === 'loading'}
                className="w-full py-3 rounded-xl bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                {formStatus === 'loading' ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i>
                    提交中...
                  </span>
                ) : (
                  '提交留言'
                )}
              </button>
            </form>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8">
              <div className="text-center p-4">
                <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-2">
                  <i className="ri-mail-line text-primary-500"></i>
                </div>
                <p className="text-xs text-foreground-500">contact@hanman.example.com</p>
              </div>
              <div className="text-center p-4">
                <div className="w-10 h-10 rounded-full bg-accent-100 flex items-center justify-center mx-auto mb-2">
                  <i className="ri-wechat-line text-accent-500"></i>
                </div>
                <p className="text-xs text-foreground-500">微信公众号：韩漫网站</p>
              </div>
              <div className="text-center p-4">
                <div className="w-10 h-10 rounded-full bg-secondary-100 flex items-center justify-center mx-auto mb-2">
                  <i className="ri-customer-service-2-line text-secondary-500"></i>
                </div>
                <p className="text-xs text-foreground-500">工作日 9:00 - 18:00</p>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}